<template>
  <div class="circle-progress" @click="onClick">
    <vue-circle
      ref="progress"
      :key="sizeValue"
      :style="{ height: `${sizeValue}px` }"
      :progress="progress"
      :size="sizeValue"
      :reverse="true"
      line-cap="round"
      :fill="fill"
      :empty-fill="emptyFillValue"
      :animation-start-value="0.0"
      :start-angle="startAngle"
      insert-mode="append"
      :thickness="thicknessValue"
      :animation="{ duration: animation }"
      :show-percent="false"
    >
      <div ref="slot" :style="{ padding: `${slotPadding}px` }">
        <slot></slot>
      </div>
    </vue-circle>
  </div>
</template>

<script>
import VueCircle from "vue2-circle-progress/src/index";
import scssVars from "@/styles/general/_variables.scss";
import ResizeObserver from "resize-observer-polyfill";

const VARIANT_LIGHT = "light";
const VARIANT_SUCCESS = "success";
const VARIANT_DANGER = "danger";
const VARIANT_WARNING = "warning";
const VARIANT_LEVEL_SMALL = "level-small";
const VARIANT_LEVEL_MEDIUM = "level-medium";
const VARIANT_LEVEL_LARGE = "level-large";

const colors = {
  [VARIANT_LIGHT]: {
    fg: "#fff",
    bg: "#ffffff",
  },
  [VARIANT_SUCCESS]: {
    fg: scssVars.success,
    bg: "#ffffff",
  },
  [VARIANT_DANGER]: {
    fg: scssVars.danger,
    bg: "#ffffff",
  },
  [VARIANT_WARNING]: {
    fg: scssVars.warning,
    bg: "#ffffff",
  },
  [VARIANT_LEVEL_SMALL]: {
    fg: scssVars.small,
    bg: "#d1e0e0",
  },
  [VARIANT_LEVEL_MEDIUM]: {
    fg: scssVars.medium,
    bg: "#cfdae2",
  },
  [VARIANT_LEVEL_LARGE]: {
    fg: scssVars.large,
    bg: "#dddadf",
  },
};

export default {
  name: "AppCircleProgress",
  components: {
    VueCircle,
  },
  props: {
    progress: {
      type: Number,
      required: false,
      default: 0,
    },
    variant: {
      type: String,
      required: false,
      default: VARIANT_LIGHT,
      validator(value) {
        return (
          [
            VARIANT_LIGHT,
            VARIANT_SUCCESS,
            VARIANT_DANGER,
            VARIANT_WARNING,
            VARIANT_LEVEL_SMALL,
            VARIANT_LEVEL_MEDIUM,
            VARIANT_LEVEL_LARGE,
          ].indexOf(value) >= 0
        );
      },
    },
    emptyFillOpacity: {
      type: Number,
      required: false,
      default: 0.15,
    },
    emptyFill: {
      type: String,
      required: false,
      default: null,
    },
    animation: {
      type: Number,
      required: false,
      default: 1000,
    },
    size: {
      type: [Number, String],
      required: false,
      default: 0,
      validator(value) {
        if (typeof value === "string") {
          return !isNaN(parseFloat(value));
        }

        return true;
      },
    },
    thickness: {
      type: Number,
      required: false,
      default: null,
    },
  },
  mounted() {
    if (!this.size && this.$el) {
      this.sizeValue = this.$el.clientWidth;

      this.resizeObserver = new ResizeObserver(() => {
        this.sizeValue = this.$el.clientWidth;
      });
      this.resizeObserver.observe(this.$el);
    }
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  },
  methods: {
    onClick(event) {
      this.$emit("click", event);
    },
    convertSizeToPx(size) {
      if (typeof size === "string") {
        if (size.indexOf("rem") >= 0) {
          return this.convertRemToPx(size);
        }
      }

      return parseFloat(size);
    },
    convertRemToPx(rem) {
      return (
        rem * parseFloat(getComputedStyle(document.documentElement).fontSize)
      );
    },
  },
  computed: {
    thicknessValue() {
      if (this.thickness) {
        return this.thickness;
      }

      return this.sizeValue / 20;
    },
    fill() {
      return { color: this.color.fg };
    },
    emptyFillValue() {
      if (this.emptyFill) {
        return this.emptyFill;
      }

      return [this.color.bg, this.emptyFillOpacityHex].join("");
    },
    color() {
      return colors[this.variant] || colors[VARIANT_LIGHT];
    },
    emptyFillOpacityHex() {
      const hex = Math.ceil(255 * this.emptyFillOpacity).toString(16);
      return hex.length < 2 ? `0${hex}` : hex;
    },
    slotPadding() {
      return Math.ceil(this.sizeValue * 0.2);
    },
  },
  watch: {
    size: {
      handler(value) {
        this.sizeValue = this.convertSizeToPx(value);
      },
      immediate: true,
    },
    progress(value) {
      this.$refs.progress.updateProgress(value);
    },
    variant() {
      this.$refs.progress.updateFill(this.fill);
    },
  },
  data() {
    return {
      startAngle: -1 * 90 * (Math.PI / 180),
      sizeValue: 0,
    };
  },
};
</script>

<style lang="scss" scoped>
.circle-progress {
  width: 100%;
  height: 100%;
}
</style>
